import type { ReactNode } from "react";
import { AppLink, cx, formatRelative } from "@amber/ui";
import { HEALTH_LABELS, HEALTH_ORDER, type SourceHealthKey, type SourcesResponse } from "../../contracts/index";
import { listOf, toggled } from "../filterList";
import type { SourcesSearch } from "../search";
import { HealthMark } from "./SourceVisuals";

function Count({
  n,
  label,
  tone,
  on,
  onClick,
  icon,
}: {
  n: number;
  label: string;
  tone?: "attention" | "waiting";
  on?: boolean;
  onClick?: () => void;
  icon?: ReactNode;
}) {
  const body = (
    <>
      <b
        className={cx(
          "text-2xl font-semibold",
          tone === "attention" && n > 0 && "text-attention",
          tone === "waiting" && n > 0 && "text-waiting",
        )}
      >
        {n}
      </b>
      <span className="inline-flex items-center gap-1.5 text-ui text-muted">
        {icon}
        {label}
      </span>
    </>
  );
  if (!onClick) return <span className="inline-flex items-baseline gap-1.5 px-1 py-0.5">{body}</span>;
  return (
    <button
      type="button"
      aria-pressed={on ?? false}
      onClick={onClick}
      className={cx(
        "inline-flex items-baseline gap-1.5 rounded-md px-1 py-0.5 hover:bg-surface-2",
        on && "bg-accent-soft",
      )}
    >
      {body}
    </button>
  );
}

function healthTone(h: SourceHealthKey): "attention" | "waiting" | undefined {
  if (h === "failing" || h === "partial") return "attention";
  return h === "late" ? "waiting" : undefined;
}

function ScanStatus({ counts }: { counts: SourcesResponse["counts"] }) {
  if (counts.scanning.length > 0) {
    return (
      <span>
        Scanning now: <b className="font-semibold text-fg">{counts.scanning.join(", ")}</b>
      </span>
    );
  }
  if (counts.next_scan_at) return <span>Next scheduled scan {formatRelative(counts.next_scan_at)}</span>;
  return "Nothing is scheduled";
}

export function HealthStrip({
  data,
  search,
  set,
  gapsOn,
  onGaps,
}: {
  data: SourcesResponse;
  search: SourcesSearch;
  set: (patch: Partial<SourcesSearch>) => void;
  gapsOn: boolean;
  onGaps: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pt-1 pb-1 max-compact:px-2">
      <ul className="m-0 flex list-none flex-wrap items-center gap-x-3 gap-y-1 p-0" aria-label="Sources by health">
        <li>
          <Count n={data.counts.sources} label="sources" />
        </li>
        {HEALTH_ORDER.map((h) => {
          const label = HEALTH_LABELS[h].toLowerCase();
          const t = healthTone(h);
          return (
            <li key={h}>
              <Count
                n={data.counts.by_health[h] ?? 0}
                label={label}
                {...(t ? { tone: t } : {})}
                icon={<HealthMark state={h} />}
                on={listOf(search.health).includes(h)}
                onClick={() => {
                  set({ health: toggled(search.health, h) });
                }}
              />
            </li>
          );
        })}
        <li>
          <Count n={data.counts.open_gaps} label="open gaps" on={gapsOn} onClick={onGaps} />
        </li>
      </ul>
      <span className="flex-1" />
      {data.extension ? (
        <AppLink
          href={data.extension.href}
          className="inline-flex items-center gap-2 rounded-full border border-divider px-3 py-1 text-md text-link"
        >
          {data.extension.text}
        </AppLink>
      ) : null}
      <p className="m-0 inline-flex items-center gap-2 rounded-full border border-divider px-3 py-1 text-md text-muted max-narrow:hidden">
        <span
          aria-hidden="true"
          className={cx("size-2 rounded-full", data.counts.scanning.length > 0 ? "bg-link" : "bg-surface-3")}
        />
        <ScanStatus counts={data.counts} />
      </p>
    </div>
  );
}

export function PlatformsStrip({ data }: { data: SourcesResponse }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 pb-1">
      <Count n={data.platforms.filter((p) => p.collection_state === "collected").length} label="collected platforms" />
      <Count
        n={
          data.platforms.filter(
            (p) => p.health.state === "failing" || p.health.state === "partial" || p.health.state === "late",
          ).length
        }
        label="with problems"
        tone="attention"
      />
      <Count n={data.platforms.filter((p) => p.health.state === "unusually_quiet").length} label="quiet" />
      <Count n={data.platforms.filter((p) => p.health.state === "healthy").length} label="healthy" />
      <Count n={data.platforms.filter((p) => p.collection_state !== "collected").length} label="not collected yet" />
      <Count n={data.platforms.filter((p) => p.issue !== null).length} label="platform-wide issues" tone="waiting" />
      <span className="text-md text-muted">
        All time:{" "}
        <AppLink href="/backlog" className="text-link underline">
          <b className="font-mono">{data.platforms.reduce((n, p) => n + p.items_total, 0).toLocaleString("en-GB")}</b>{" "}
          items
        </AppLink>
      </span>
    </div>
  );
}
